import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from './ThemeContext';
import Home from './Home';
import BusinessList from './BusinessList';
import BusinessDetail from './BusinessDetail';
import Register from './Register';
import BusinessMap from './BusinessMap';
import BusinessRegister from './BusinessRegister';
import Login from './Login';
import AdminDashboard from './AdminDashboard';
import BusinessOwnerDashboard from './BusinessOwnerDashboard';
import EmployeeDashboard from './EmployeeDashboard';
import LandingPage from './LandingPage';
import BusinessOverview from './BusinessOverview';
import { ForgotPassword, ResetPassword } from './AuthPages';
import CustomerSettings from './CustomerSettings';


function FloatingThemeToggle() {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9999,
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
        background: isDark ? 'rgba(20,10,40,0.85)' : 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.6)',
        fontSize: '16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.12)',
        padding: 0,
        lineHeight: 1,
        fontFamily: 'inherit',
      }}
    >
      {isDark ? '☀' : '☾'}
    </button>
  );
}

function App() {
  useEffect(() => {
    // Remove all default margins and padding
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    // Optional: Remove default browser styles completely
    document.body.style.boxSizing = 'border-box';
    document.documentElement.style.boxSizing = 'border-box';
  }, []);

  const [customer, setCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('rn_customer');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const [selectedBusiness, setSelectedBusiness] = useState(() => {
    try {
      const saved = sessionStorage.getItem('rn_business');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (customer) {
      localStorage.setItem('rn_customer', JSON.stringify(customer));
    } else {
      localStorage.removeItem('rn_customer');
    }
  }, [customer]);

  useEffect(() => {
    if (selectedBusiness) {
      sessionStorage.setItem('rn_business', JSON.stringify(selectedBusiness));
    } else {
      sessionStorage.removeItem('rn_business');
    }
  }, [selectedBusiness]);

  const handleLogin = (data) => {
    setCustomer(data);
    navigate('/home');
  };

  const handleCustomerUpdate = (updated) => {
    setCustomer(updated);
  };

  const handleLogout = () => {
    setCustomer(null);
    setSelectedBusiness(null);
    localStorage.removeItem('rn_customer');
    sessionStorage.clear();
    navigate('/');
  };

  const handleSelectBusiness = (biz) => {
    setSelectedBusiness(biz);
    navigate('/business');
  };

  // Redirects unauthenticated customers to the login page
  const Protected = ({ children }) => {
    if (!customer) return <Navigate to="/signin" replace />;
    return children;
  };

  // Redirects already-authenticated customers away from staff/admin portals.
  // Unauthenticated visitors still reach the portal's own login form.
  const StaffRoute = ({ children }) => {
    if (customer) return <Navigate to="/home" replace />;
    return children;
  };

  const BusinessDetailWrapper = () => {
    if (!selectedBusiness) return <Navigate to="/businesses" replace />;
    return (
      <BusinessDetail
        business={selectedBusiness}
        customer={customer}
        onBack={() => navigate('/businesses')}
        onLogout={handleLogout}
        onRefresh={() => setRefreshKey(k => k + 1)}
      />
    );
  };

  return (
    <ThemeProvider>
    <FloatingThemeToggle />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/signin" element={<Login onLogin={handleLogin} />} />
      <Route path="/register" element={<Register onRegister={handleLogin} onBack={() => navigate('/signin')} />} />
      <Route path="/business-overview" element={<BusinessOverview />} />
      <Route path="/business-register" element={<BusinessRegister onBack={() => navigate('/business-overview')} onSuccess={() => setRefreshKey(k => k + 1)} />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/home" element={<Protected><Home customer={customer} onLogout={handleLogout} onNavigate={navigate} refreshKey={refreshKey} /></Protected>} />
      <Route path="/settings" element={<Protected><CustomerSettings customer={customer} onCustomerUpdate={handleCustomerUpdate} onLogout={handleLogout} /></Protected>} />
      <Route path="/businesses" element={<Protected><BusinessList key={refreshKey} customer={customer} onLogout={handleLogout} onSelectBusiness={handleSelectBusiness} onNavigate={navigate} /></Protected>} />
      <Route path="/business" element={<Protected><BusinessDetailWrapper /></Protected>} />
      <Route path="/map" element={<Protected><BusinessMap key={refreshKey} customer={customer} onLogout={handleLogout} onNavigate={navigate} onSelectBusiness={handleSelectBusiness} /></Protected>} />

      <Route path="/admin" element={<StaffRoute><AdminDashboard /></StaffRoute>} />
      <Route path="/business-owner" element={<StaffRoute><BusinessOwnerDashboard /></StaffRoute>} />
      <Route path="/employee" element={<StaffRoute><EmployeeDashboard /></StaffRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ThemeProvider>
  );
}

export default App;
